import {
    pxToRem,
    pxArrayToRem,
    captionClassName,
    labelClassName,
    archizeenTileClassName,
    flexClassName,
    buttonClassName,
    paginationClassName,
    dividerClassName,
    paginationItemCounterClassName,
    paginationItemsPerPageClassName,
    paginationSlotClassNames,
    SubheaderClassName,
    tileImageClassName,
    dropdownClassName,
} from '@archipro-design/aria';

import type { StyleRule } from '@archipro-design/aria';

export const TILE_WIDTH_DESKTOP = 368;

export const ArticleSection: StyleRule = ({ theme }) => ({
    [theme.screen.laptop]: {
        marginBottom: pxToRem(240),
    },
});

export const ArticleInner: StyleRule = ({ theme }) => ({
    [theme.screen.laptop]: {
        marginTop: pxToRem(41),
    },
});

export const Header: StyleRule = () => ({
    [`& > div`]: {
        padding: pxArrayToRem([0, 36]),
    },
});

export const Container: StyleRule = ({ theme }) => ({
    [theme.screen.laptop]: {
        marginBottom: 0,
    },
});

export const FilterBar: StyleRule = ({ theme }) => ({
    height: pxToRem(60),
    padding: 0,
    borderBottom: `1px solid ${theme.siteVariables.colors.transparency.black['10%']}`,

    [`& .${buttonClassName}`]: {
        height: pxToRem(44),
        width: pxToRem(106),
        margin: pxArrayToRem([0, 0, 0, 18]),
        padding: 0,
    },

    [theme.screen.laptop]: {
        height: pxToRem(114),
        padding: pxArrayToRem([20, 0, 0]),
        marginTop: pxToRem(1),
        marginBottom: 0,

        borderBottom: 'none',
        borderTop: `2px solid ${theme.siteVariables.colorScheme.default?.foreground}`,
        [`& .${dropdownClassName}`]: {
            marginLeft: pxToRem(-4),
        },

        [`& > .${flexClassName}`]: {
            height: pxToRem(46),
        },
    },
});

export const MobileArticleTile: StyleRule = ({ theme }) => ({
    position: `relative`,
    paddingBottom: pxToRem(48),
    marginTop: pxToRem(-17), // To compensate for the padding of the first tile

    [`& .${labelClassName}`]: {
        color: theme.siteVariables.colorScheme.default?.foreground,
    },

    [`& .${captionClassName}`]: {
        lineHeight: 1.5,
    },

    [`& a.${archizeenTileClassName}`]: {
        borderBottom: `1px solid ${theme.siteVariables.colors.grey[150]}`,
        padding: pxArrayToRem([17, 18]),

        [`& > :last-child`]: {
            borderBottom: 'none',
        },

        [`& > .${flexClassName}`]: {
            [`& > .${flexClassName}`]: {
                padding: pxArrayToRem([0, 4, 8, 0]),
                [`& > .${flexClassName}`]: {
                    paddingTop: pxToRem(2),
                },
            },
        },
    },
});

export const DesktopArticleTile: StyleRule<{
    hasPagination: boolean;
    totalCount: number;
}> = ({ theme, hasPagination, totalCount }) => ({
    position: `relative`,
    rowGap: pxToRem(115),
    marginTop: pxToRem(-14),
    ...(hasPagination && { paddingBottom: pxToRem(126) }),

    [`& .${labelClassName}`]: {
        color: theme.siteVariables.colorScheme.default?.foreground,
    },
    [`& > div`]: {
        maxWidth: '100%',
        padding: pxArrayToRem([14, 28, 14, 0]),
    },

    [`& a.${archizeenTileClassName}`]: {
        padding: 0,
        width: pxToRem(TILE_WIDTH_DESKTOP),
        maxWidth: '100%',

        [`& .${tileImageClassName}`]: {
            marginBottom: pxToRem(20),
        },

        [`& .${SubheaderClassName}`]: {
            letterSpacing: theme.siteVariables.letterSpacingSmall,
            marginBottom: pxToRem(19),
        },

        [`& .${captionClassName}`]: {
            lineHeight: 1.5,
            marginBottom: pxToRem(3),
        },
    },
    [`& > div:nth-last-child(-n+${totalCount % 5})`]: {
        paddingBottom: 0,
    },
});

export const DesktopPaginationWrapper: StyleRule = () => ({
    padding: pxArrayToRem([0, 0, 0, 27]),
    [`& .${paginationClassName}`]: {
        width: 'auto',
        [`& .${paginationSlotClassNames.list}`]: {
            marginBottom: pxToRem(25),
        },
        [`& .${paginationItemCounterClassName}`]: {
            margin: 0,
        },
    },

    [`& .${dividerClassName}`]: {
        display: 'none',
    },

    [`& .${paginationItemsPerPageClassName}`]: {
        display: 'none',
    },
});

export const FilterTogglesSection: StyleRule = ({ theme }) => ({
    [theme.screen.laptop]: {
        height: pxToRem(81),
    },
});

export const FilterSection: StyleRule = ({ theme }) => ({
    [theme.screen.laptop]: {
        paddingTop: pxToRem(81),
    },
});
